import { createDomain } from '@/api/domains';
import LoadingWrapper from '@/components/loading-wrapper';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { InputGroup, InputGroupInput } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import type { IUserDomain } from '@/entities/domain.entity';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';

interface AddDomainModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onCreated: (domain?: IUserDomain) => void;
}

export default function AddDomainModal({ open, onOpenChange, onCreated }: AddDomainModalProps) {
	const [hostname, setHostname] = useState('');
	const [isSaving, setIsSaving] = useState(false);

	function handleOpenChange(next: boolean) {
		if (!next) setHostname('');
		onOpenChange(next);
	}

	async function handleSubmit(e: FormEvent) {
		e.preventDefault();

		const trimmed = hostname.trim().toLowerCase();
		if (!trimmed) {
			toast.error('Введите доменное имя');
			return;
		}

		setIsSaving(true);
		try {
			const created = await createDomain(trimmed);
			toast.success('Домен добавлен');
			setHostname('');
			onOpenChange(false);
			onCreated(created);
		} finally {
			setIsSaving(false);
		}
	}

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className='sm:max-w-[440px]'>
				<DialogHeader>
					<DialogTitle>Добавить домен</DialogTitle>
				</DialogHeader>

				<form className='flex flex-col gap-4' onSubmit={handleSubmit}>
					<div className='flex flex-col gap-1.5'>
						<Label htmlFor='domain-hostname'>Доменное имя</Label>
						<InputGroup>
							<InputGroupInput
								id='domain-hostname'
								placeholder='app.example.com'
								value={hostname}
								onChange={e => setHostname(e.target.value)}
								disabled={isSaving}
								autoComplete='off'
							/>
						</InputGroup>
						<p className='text-xs text-muted-foreground'>
							Укажите поддомен или корневой домен. После добавления настройте NS-записи у регистратора.
						</p>
					</div>

					<DialogFooter>
						<LoadingWrapper show={isSaving}>
							<Button type='submit' disabled={isSaving}>
								Добавить
							</Button>
						</LoadingWrapper>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
